import { FormControl, InputLabel } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiCreateInternalUser, apiUpdateInternalUser } from '~/providers/api';
import { IFetchOutput } from "~/interfaces";
import { IInternalUser, IUserState, TInternalUserRole } from "~/providers/interfaces";
import { MySelect, MyMenuItem } from "~/mui/MySelect";
import TextFieldEnter from "~/mui/TextFieldEnter";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IStateUser extends Pick<IUserState, "email" | "internalRole"> {}

// chyby formulare
type TStateErrors = {[key in keyof Pick<IStateUser, "email">]: string; };

interface ICreateInternalUserModal {
	userData: IInternalUser;
	variant: "create" | "update";
	onSave?: (userData: IInternalUser) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function CreateInternalUserModal({
	userData = null,
	variant = "create",
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: ICreateInternalUserModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateUser, updateState: updateStateUser } = myUseState<IStateUser>({
		email: userData.email,
		internalRole: userData.internalRole,
	});
	const { state: stateError, setState: setStateError } = myUseState<TStateErrors>({
		email: "",
	});
	const isNew = userData.id === 0;

	function getWasChanged(newState: Partial<IStateUser>): boolean {
		const newStateUser = {
			...stateUser,
			...newState,
		};

		return newStateUser.email !== userData.email
			|| newStateUser.internalRole !== userData.internalRole;
	}

	function updateStateUserWithChange(newState: Partial<IStateUser>) {
		const wasChanged = getWasChanged(newState);

		updateStateUser(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		const errorData = checkForErrorsState<IStateUser, TStateErrors>(stateUser, {
			email: { check: "email", msg: trans("userRole.error.email") },
		});

		setStateError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		let outputData: IFetchOutput<IInternalUser> = null;

		if (isNew) {
			outputData = await apiCreateInternalUser({
				...stateUser,
			}, lang);
		} else {
			outputData = await apiUpdateInternalUser(userData.id, {
				internalRole: stateUser.internalRole,
			}, lang);
		}

		if (outputData.noErrors) {
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={isNew ? trans("createInternalUserModal.newTitle") : trans("createInternalUserModal.editTitle") } onClose={onClose} onSave={dialogOnSave} className="projectSettings"
		dialogLoading={state.dialogLoading} disableSave={!state.wasChanged} errorMsg={state.errorMsg} confirmClose={state.wasChanged} minWidth="300px">
		{ variant === "create" && <TextFieldEnter
			label={trans("createInternalUserModal.email")}
			value={stateUser.email}
			style={{ width: "100%" }}
			onEnter={() => dialogOnSave()}
			onChange={event => updateStateUserWithChange({ email: event.target.value })}
			color="success"
			autoComplete="off"
			error={stateError.email !== ""}
			helperText={stateError.email}
			sx={{ marginBottom: "var(--margin-large)" }}
		/> }
		<FormControl sx={{ width: "100%" }}>
			<InputLabel>{ trans("createInternalUserModal.internalRole") }</InputLabel>
			<MySelect
				value={stateUser.internalRole}
				onChange={event => updateStateUserWithChange({ internalRole: event.target.value as TInternalUserRole })}
				label={ trans("createInternalUserModal.internalRole") }
				size="small"
				sx={{ width: "100%" }}
			>
				<MyMenuItem value="internal_admin">{ trans("createInternalUserModal.internalRole.internalAdmin") }</MyMenuItem>
				<MyMenuItem value="salesman">{ trans("createInternalUserModal.internalRole.salesman") }</MyMenuItem>
			</MySelect>
		</FormControl>
	</MyDialog>;
}
